import { provideHttpClient, withInterceptors } from '@angular/common/http';
import { provideZoneChangeDetection } from '@angular/core';
import { bootstrapApplication } from '@angular/platform-browser';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { provideRouter, withHashLocation } from '@angular/router';
import { ModuleRegistry } from 'ag-grid-community';
import { AllEnterpriseModule, LicenseManager } from 'ag-grid-enterprise';
import { Amplify } from 'aws-amplify';
import { fetchAuthSession, signInWithRedirect, signOut } from 'aws-amplify/auth';
import { LICENSE } from 'wilshire-ui';

import { App } from './app/app';
import { routes } from './app/app.routes';
import { jwtInterceptor } from './app/core/interceptors/jwt.interceptor';
import { errorInterceptor } from './app/core/interceptors/error.interceptor';
import { environment } from './environments/environment';
import { Env } from './app/core/enums/env.enum';

// AG Grid License
LicenseManager.setLicenseKey(LICENSE);
// Register all enterprise features
ModuleRegistry.registerModules([AllEnterpriseModule]);

// Configure AWS Amplify
Amplify.configure({
  Auth: {
    Cognito: {
      userPoolId: environment.cognito.USERPOOL_ID,
      userPoolClientId: environment.cognito.CLIENT_ID,
      loginWith: {
        oauth: {
          domain: environment.cognito.APP_DOMAIN,
          scopes: ['phone', 'email', 'profile', 'openid'],
          redirectSignIn: [environment.cognito.REDIRECT_URI],
          redirectSignOut: [environment.cognito.REDIRECT_URI_SIGNOUT],
          responseType: 'code',
        },
      },
    },
  },
});

/**
 * Initialize the application
 *
 * Checks authentication and group membership BEFORE bootstrapping the app.
 * This prevents any UI from rendering until the user is authenticated and authorized.
 *
 */
async function initializeApp() {
  try {
    const { idToken, accessToken } = (await fetchAuthSession()).tokens ?? {};

    if (!accessToken) {
      throw new Error('No authenticated user found');
    }

    // Extract Cognito groups from access token
    const groups = (accessToken.payload['cognito:groups'] as string[]) || [];
    const upperCaseGroups = groups.map((group) => group.toUpperCase());

    // Check if user is in required group (case-insensitive)
    const requiredGroup = environment.cognitoGroupName.toUpperCase();
    const isAuthorizedUser =
      Array.isArray(upperCaseGroups) && upperCaseGroups.includes(requiredGroup);

    if (isAuthorizedUser) {
      // Log successful authorization in non-production
      if (!environment.production) {
        const userEmail = idToken?.payload['email']?.toString() ?? 'User';
        console.log(`${userEmail} is authorized (${environment.cognitoGroupName} group)`);
      }

      // Bootstrap the application
      bootstrapApplication(App, {
        providers: [
          provideZoneChangeDetection({ eventCoalescing: true }),
          provideRouter(routes, withHashLocation()),
          provideHttpClient(withInterceptors([jwtInterceptor, errorInterceptor])),
        ],
      });
    } else {
      // User authenticated but not in required group
      if (!environment.production) {
        const userEmail = idToken?.payload['email']?.toString() ?? 'User';
        console.error(
          `${userEmail} is not authorized. Required group: ${environment.cognitoGroupName}, User groups: ${groups.join(', ')}`,
        );
      }

      await signOut();
    }
  } catch (err) {
    console.log('Not signed in, redirecting to login');
    if (!environment.production) {
      console.error(err);
    }
    signInWithRedirect();
  }
}

// Start the app initialization
initializeApp();
